import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tabs,
  Tab,
  Box,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, ArrowBack as ArrowBackIcon, Edit as EditIcon, AssignmentInd as AssignmentIndIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../../services/authService';
import CommunicationsTab from '../../../components/CommunicationsTab';

const AccountSettings = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', role: 'Account Member', campaigns: []  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [accountData, setAccountData] = useState({ name: '', status: 'active' }); // Manage account form data
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // Dialog for delete confirmation

  // New state variables for managing campaigns
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCampaignsDialogOpen, setUserCampaignsDialogOpen] = useState(false);
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [newApiKeyDialogOpen, setNewApiKeyDialogOpen] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKey, setNewApiKey] = useState(null);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);

  // New state variables for contact endpoints
  const [contactEndpoints, setContactEndpoints] = useState([]);
  const [newEndpointDialogOpen, setNewEndpointDialogOpen] = useState(false);
  const [newEndpoint, setNewEndpoint] = useState({
    channel: 'email',
    value: '',
    platform: '',
    label: '',
    priority: 'primary',
    is_primary: false,
    is_verified: false,
    account: null,
    campaign: null,
    funnel: null
  });

  useEffect(() => {
    fetchAccountData();
    fetchUsers();
    fetchCampaigns();
    fetchApiKeys();
    fetchContactEndpoints();
  }, [accountId]);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchAccountData = async () => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/`);
      const { name, description, active } = response.data;

      // Set account data with the appropriate string value for the status
      setAccountData({
        name: name,
        description: description,
        status: active ? 'active' : 'inactive',  // Map boolean to 'active' or 'inactive'
      });
    } catch (error) {
      console.error('Error fetching account data:', error);
      showSnackbar('Error fetching account data', 'error');
    }
  };


  const handleUpdateAccount = async () => {
    try {
      // Convert 'active'/'inactive' back to boolean before sending to the backend
      const updatedAccountData = {
        ...accountData,
        active: accountData.status === 'active',
      };

      await api.put(`/crm/accounts/${accountId}/`, updatedAccountData);
      showSnackbar('Account updated successfully', 'success');
      fetchAccountData(); // Refresh the account data after update
    } catch (error) {
      console.error('Error updating account:', error);
      showSnackbar('Error updating account', 'error');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete(`/crm/accounts/${accountId}/`);
      showSnackbar('Account deleted successfully', 'success');
      navigate('/crm/accounts'); // Redirect to accounts list
    } catch (error) {
      console.error('Error deleting account:', error);
      showSnackbar('Error deleting account', 'error');
    }
  };

  const handleAddUser = async () => {
    try {
      const payload = {
        email: newUser.email,
        role: newUser.role,
        campaigns: newUser.campaigns,
      };
      await api.post(`/crm/accounts/${accountId}/add_user/`, payload);
      setNewUser({ email: '', role: 'Account Member', campaigns: [] });
      setUserDialogOpen(false);
      fetchUsers();
      showSnackbar('User added to account successfully', 'success');
    } catch (error) {
      console.error('Error adding user to account:', error);
      showSnackbar('Error adding user to account', 'error');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/crm/accounts/${accountId}/update_user_role/`, { user_id: userId, role: newRole });
      fetchUsers();
      showSnackbar('User role updated successfully', 'success');
    } catch (error) {
      console.error('Error updating user role:', error);
      showSnackbar('Error updating user role', 'error');
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await api.delete(`/crm/accounts/${accountId}/remove_user/`, {
        data: { user_id: userId } // Pass the user_id in the request data
      });
      fetchUsers();
      showSnackbar('User removed from account successfully', 'success');
    } catch (error) {
      console.error('Error removing user from account:', error);
      showSnackbar('Error removing user from account', 'error');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/users/`);
      // Assuming your API returns { users: [...] }
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching account users:', error);
      showSnackbar('Error fetching account users', 'error');
    }
  };

  // Fetch all campaigns associated with the account
  const fetchCampaigns = async () => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/campaigns/`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      showSnackbar('Error fetching campaigns', 'error');
    }
  };

  // Handle opening the manage campaigns dialog
  const handleManageCampaigns = (user) => {
    setSelectedUser(user);
    // Get IDs of campaigns assigned to the user
    setUserCampaigns(user.campaign_access.map((c) => c.campaign_id));
    setUserCampaignsDialogOpen(true);
  };

  const handleCampaignCheckboxChange = (event, campaignId) => {
    if (event.target.checked) {
      setUserCampaigns([...userCampaigns, campaignId]);
    } else {
      setUserCampaigns(userCampaigns.filter((id) => id !== campaignId));
    }
  };


  const handleUpdateUserCampaigns = async () => {
    try {
      const currentCampaignIds = selectedUser.campaign_access.map((c) => c.campaign_id);
  
      // Campaigns to add (newly selected campaigns that the user does not already have access to)
      const campaignsToAdd = userCampaigns.filter(
        (campaignId) => !currentCampaignIds.includes(campaignId)
      );
  
      // Campaigns to remove (existing campaigns that are no longer selected)
      const campaignsToRemove = currentCampaignIds.filter(
        (campaignId) => !userCampaigns.includes(campaignId)
      );
  
      // Add campaigns
      for (const campaignId of campaignsToAdd) {
        await api.post(`/crm/accounts/${accountId}/add_user_to_campaign/`, {
          user_id: selectedUser.user.id,
          campaign_id: campaignId,
        });
      }
  
      // Remove campaigns
      for (const campaignId of campaignsToRemove) {
        await api.delete(`/crm/accounts/${accountId}/remove_user_from_campaign/`, {
          data: {
            user_id: selectedUser.user.id,
            campaign_id: campaignId,
          },
        });
      }
  
      // Close the dialog and show success message
      setUserCampaignsDialogOpen(false);
      showSnackbar('User campaigns updated successfully', 'success');
      fetchUsers(); // Refresh the user data
    } catch (error) {
      console.error('Error updating user campaigns:', error);
      showSnackbar('Error updating user campaigns', 'error');
    }
  };
  

  const fetchApiKeys = async () => {
    try {
      const response = await api.get(`/reporting/api-keys/`, {
        params: {
          account_id: accountId
        }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      showSnackbar('Error fetching API keys', 'error');
    }
  };

  const handleCreateApiKey = async () => {
    try {
      const response = await api.post('/reporting/api-keys/', {
        name: newApiKeyName,
        account_id: accountId
      });
      setNewApiKey(response.data);
      setShowApiKeyDialog(true);
      setNewApiKeyDialogOpen(false);
      setNewApiKeyName('');
      fetchApiKeys();
      showSnackbar('API key created successfully', 'success');
    } catch (error) {
      console.error('Error creating API key:', error);
      showSnackbar('Error creating API key', 'error');
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    try {
      await api.delete(`/reporting/api-keys/${keyId}/`);
      fetchApiKeys();
      showSnackbar('API key deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting API key:', error);
      showSnackbar('Error deleting API key', 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard', 'success');
  };

  const fetchContactEndpoints = async () => {
    try {
      const response = await api.get('/communications/contact-endpoints/', {
        params: { account_id: accountId }
      });
      setContactEndpoints(response.data);
    } catch (error) {
      console.error('Error fetching contact endpoints:', error);
      showSnackbar('Error fetching contact endpoints', 'error');
    }
  };

  const handleCreateEndpoint = async () => {
    try {
      const payload = {
        ...newEndpoint,
        account: accountId
      };
      await api.post('/communications/contact-endpoints/', payload);
      setNewEndpointDialogOpen(false);
      setNewEndpoint({
        channel: 'email',
        value: '',
        platform: '',
        label: '',
        priority: 'primary',
        is_primary: false,
        is_verified: false,
        account: null,
        campaign: null,
        funnel: null
      });
      fetchContactEndpoints();
      showSnackbar('Contact endpoint created successfully', 'success');
    } catch (error) {
      console.error('Error creating contact endpoint:', error);
      showSnackbar('Error creating contact endpoint', 'error');
    }
  };

  const handleDeleteEndpoint = async (endpointId) => {
    try {
      await api.delete(`/communications/contact-endpoints/${endpointId}/`);
      fetchContactEndpoints();
      showSnackbar('Contact endpoint deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting contact endpoint:', error);
      showSnackbar('Error deleting contact endpoint', 'error');
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/crm/accounts')}
        sx={{ mb: 2 }}
      >
        Back to Accounts
      </Button>
      <Typography variant="h4" gutterBottom color="textPrimary">
        Account Settings: {accountData?.name}
      </Typography>
  
      {/* Tabs for different sections */}
      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
        <Tab label="General Information" />
        <Tab label="User Management" />
        <Tab label="API Keys" />
        <Tab label="Communications" />
      </Tabs>
  
      <Box sx={{ padding: 2 }}>
        {/* Account Management */}
        {tabValue === 0 && (
          <>
            <Typography variant="h6" gutterBottom color="textPrimary">
              General Information
            </Typography>
            <Divider sx={{ my: 2 }} />
            <TextField
              label="Name"
              fullWidth
              value={accountData.name}
              onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              fullWidth
              value={accountData.description}
              onChange={(e) => setAccountData({ ...accountData, description: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: !!accountData.description,  // Force label to shrink when there is a value
              }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={accountData.status}
                onChange={(e) => setAccountData({ ...accountData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            {/* Buttons aligned on the same row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto' }}>
              {/* Update Account Button */}
              <Button
                variant="contained"
                onClick={handleUpdateAccount}
                sx={{
                  backgroundColor: 'primary',        // Blue background color for Update button
                  color: 'white',                 // White text color
                  ':hover': {                     // Styling on hover
                    backgroundColor: '#003366'    // Darker blue on hover
                  }
                }}
              >
                Update Account
              </Button>

              {/* Delete Account Button */}
              <Button
                variant="contained"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  backgroundColor: 'red',        // Red background color for Delete button
                  color: 'white',                // White text color
                  ':hover': {                    // Styling on hover
                    backgroundColor: '#cc0000'   // Darker red on hover
                  }
                }}
              >
                Delete Account
              </Button>
            </Box>
          </>
        )}
  
        <Box sx={{ padding: 2 }}>
        {tabValue === 1 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }} color="textPrimary">
              User Management
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Campaigns</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(users) && users.length > 0 ? (
                    users.map((userData) => (
                      <TableRow key={userData.user.id}>
                        <TableCell>{userData.user.email}</TableCell>
                        <TableCell>
                          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={userData.account_membership.role.name}
                              onChange={(e) =>
                                handleUpdateUserRole(userData.user.id, e.target.value)
                              }
                            >
                              <MenuItem value="Account Admin">Account Admin</MenuItem>
                              <MenuItem value="Account Manager">Account Manager</MenuItem>
                              <MenuItem value="Account Member">Account Member</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {userData.campaign_access && userData.campaign_access.length > 0 ? (
                            <Typography>
                              {userData.campaign_access
                                .map((campaignAccess) => campaignAccess.campaign_name ? campaignAccess.campaign_name : 'No Name')
                                .join(', ')} 
                            </Typography>
                          ) : (
                            <Typography>No Campaigns Assigned</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Assign Campaigns">
                            <IconButton onClick={() => handleManageCampaigns(userData)}>
                              <AssignmentIndIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove User">
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => handleRemoveUser(userData.user.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUserDialogOpen(true)}
              sx={{ mb: 2 }}
            >
              Add User to Account
            </Button>
          </>
        )}
        </Box>

        {/* API Keys Tab */}
        {tabValue === 2 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }} color="textPrimary">
              API Key Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewApiKeyDialogOpen(true)}
              sx={{ mb: 2 }}
            >
              Generate New API Key
            </Button>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Prefix</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell>{key.name}</TableCell>
                      <TableCell>{key.prefix}</TableCell>
                      <TableCell>{new Date(key.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        {key.last_used ? new Date(key.last_used).toLocaleString() : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={key.is_active ? 'Active' : 'Inactive'}
                          color={key.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete API Key">
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => handleDeleteApiKey(key.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Communications Tab */}
        {tabValue === 3 && (
          <CommunicationsTab 
            accountId={accountId}
            showSnackbar={showSnackbar}
          />
        )}
      </Box>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this account and all associated data?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)}>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
          <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="Account Member">Account Member</MenuItem>
              <MenuItem value="Account Manager">Account Manager</MenuItem>
              <MenuItem value="Account Admin">Account Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
            <InputLabel>Campaigns</InputLabel>
            <Select
              multiple
              value={newUser.campaigns}
              onChange={(e) => setNewUser({ ...newUser, campaigns: e.target.value })}
              label="Campaigns"
              renderValue={(selected) =>
                campaigns
                  .filter((campaign) => selected.includes(campaign.id))
                  .map((campaign) => campaign.name)
                  .join(', ')
              }
            >
              {campaigns.map((campaign) => (
                <MenuItem key={campaign.id} value={campaign.id}>
                  <Checkbox checked={newUser.campaigns.includes(campaign.id)} />
                  <ListItemText primary={campaign.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUser} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Campaigns Dialog */}
      <Dialog
        open={userCampaignsDialogOpen}
        onClose={() => setUserCampaignsDialogOpen(false)}
      >
        <DialogTitle>Manage Campaigns for {selectedUser?.user?.email}</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <FormGroup>
              {campaigns.map((campaign) => (
                <FormControlLabel
                  key={campaign.id}
                  control={
                    <Checkbox
                      checked={userCampaigns.includes(campaign.id)}
                      onChange={(e) => handleCampaignCheckboxChange(e, campaign.id)}
                    />
                  }
                  label={campaign.name}
                />
              ))}
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserCampaignsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateUserCampaigns} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* New API Key Dialog */}
      <Dialog open={newApiKeyDialogOpen} onClose={() => setNewApiKeyDialogOpen(false)}>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="API Key Name"
            fullWidth
            variant="outlined"
            value={newApiKeyName}
            onChange={(e) => setNewApiKeyName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewApiKeyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateApiKey} color="primary">
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show API Key Dialog */}
      <Dialog open={showApiKeyDialog} onClose={() => setShowApiKeyDialog(false)}>
        <DialogTitle>New API Key Generated</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please copy your API key now. You won't be able to see it again!
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <TextField
              fullWidth
              value={newApiKey?.api_key}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton
                    onClick={() => copyToClipboard(newApiKey?.api_key)}
                    edge="end"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This key will be used to authenticate your API requests. Keep it secure and never share it publicly.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApiKeyDialog(false)} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Contact Endpoint Dialog */}
      <Dialog open={newEndpointDialogOpen} onClose={() => setNewEndpointDialogOpen(false)}>
        <DialogTitle>Add New Communication Endpoint</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={newEndpoint.channel}
              onChange={(e) => setNewEndpoint({ ...newEndpoint, channel: e.target.value })}
              label="Channel"
            >
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="voice">Voice</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="social">Social Media</MenuItem>
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Value"
            fullWidth
            value={newEndpoint.value}
            onChange={(e) => setNewEndpoint({ ...newEndpoint, value: e.target.value })}
            placeholder={newEndpoint.channel === 'email' ? 'email@example.com' : 
                       newEndpoint.channel === 'social' ? '@username' : 
                       '+1234567890'}
          />

          {newEndpoint.channel === 'social' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={newEndpoint.platform}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, platform: e.target.value })}
                label="Platform"
              >
                <MenuItem value="facebook">Facebook</MenuItem>
                <MenuItem value="instagram">Instagram</MenuItem>
                <MenuItem value="twitter">Twitter</MenuItem>
                <MenuItem value="linkedin">LinkedIn</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            margin="dense"
            label="Label"
            fullWidth
            value={newEndpoint.label}
            onChange={(e) => setNewEndpoint({ ...newEndpoint, label: e.target.value })}
            placeholder="e.g., Work Phone, Personal Email"
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={newEndpoint.priority}
              onChange={(e) => setNewEndpoint({ ...newEndpoint, priority: e.target.value })}
              label="Priority"
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="backup">Backup</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={newEndpoint.is_primary}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, is_primary: e.target.checked })}
              />
            }
            label="Set as Primary"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewEndpointDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateEndpoint} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>

  );
};

export default AccountSettings